import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { SalariesPageView } from './SalariesPageView';
import { useSalariesPage } from './useSalariesPage';

/**
 * `/accounting/salaries` — the employee register and its payroll runs.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner Content
 * child, since React does not catch a component's own throws (the repo's
 * self-served page pattern).
 */
export const SalariesPage: React.FC = () => {
    const page = useSalariesPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('salaries.error.title'),
            errorMessage: t('salaries.error.message'),
            errorRetry: t('salaries.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('salaries-page')}
        >
            <ErrorBoundary
                data-testid="salaries-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="salaries-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <SalariesPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type SalariesPageContentProps = {
    page: ReturnType<typeof useSalariesPage>;
};

const SalariesPageContent: React.FC<SalariesPageContentProps> = ({ page }) => {
    if (page.error) {
        throw page.error;
    }

    // The View keeps its summary labels, toolbar and CTAs while loading and
    // skeletons only the values and rows — the repo's loading-state standard.
    return (
        <SalariesPageView
            figures={page.figures}
            isLoading={page.isLoading || page.figures === null}
            page={page}
        />
    );
};
